import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Send, Bot, User, CheckCircle2 } from 'lucide-react';
import { useAISpecificationChat } from '@/hooks/useAISpecificationChat';
import { AISpecificationSummary } from './AISpecificationSummary';

interface AISpecificationChatProps {
  productCategoryId: string;
  productCategoryName: string;
  onSave: (specData: any) => void;
  onBack: () => void;
  onCancel: () => void;
}

export function AISpecificationChat({
  productCategoryId,
  productCategoryName,
  onSave,
  onBack,
  onCancel
}: AISpecificationChatProps) {
  const [input, setInput] = useState('');
  const [extractedSpec, setExtractedSpec] = useState<any>(null);
  const [showSummary, setShowSummary] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasLoadingRef = useRef(false);
  
  const { messages, isLoading, streamingContent, sendMessage, extractSpecification, reset } = 
    useAISpecificationChat(productCategoryId);

  useEffect(() => {
    // Send initial greeting
    if (messages.length === 0) {
      sendMessage(`Hello! I need help creating a specification for ${productCategoryName}.`);
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  useEffect(() => {
    // Restore focus when loading completes (isLoading: true → false)
    if (wasLoadingRef.current && !isLoading) {
      inputRef.current?.focus();
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  const handleSend = () => {
    if (input.trim()) {
      sendMessage(input);
      setInput('');
      // Keep focus on input for continued typing
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleComplete = async () => {
    try {
      const extracted = await extractSpecification();
      setExtractedSpec(extracted);
      setShowSummary(true);
    } catch (error) {
      console.error('Error completing specification:', error);
    }
  };

  const handleRevise = () => {
    setShowSummary(false);
    setExtractedSpec(null);
  };

  if (showSummary && extractedSpec) {
    return (
      <AISpecificationSummary
        extractedData={extractedSpec}
        productCategoryName={productCategoryName}
        onSave={onSave}
        onRevise={handleRevise}
        onCancel={onCancel}
      />
    );
  }

  const allMessages = [...messages];
  if (streamingContent) {
    allMessages.push({ role: 'assistant', content: streamingContent });
  }

  return (
    <div className="space-y-4 h-[700px] flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">AI Specification Assistant</h3>
          <p className="text-sm text-muted-foreground">
            Creating specification for {productCategoryName}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
      </div>

      {/* Chat messages */}
      <Card className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <CardContent className="p-4 space-y-4">
            {allMessages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && !streamingContent && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>
        </ScrollArea>
      </Card>

      {/* Input area */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder="Type your message..."
            disabled={isLoading}
          />
          <Button onClick={handleSend} disabled={isLoading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleComplete} 
            disabled={isLoading || messages.length < 3}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            Complete Specification
          </Button>
        </div>
      </div>
    </div>
  );
}
